// Taper-chart view window logic. Pure helpers extracted so the
// window-resolution math (clamp behavior, smart-default tiers) can be
// unit-tested in the node-only vitest env without dragging in a DOM.
//
// The taper chart on /app/taper renders the full taper plan (~85 days
// for a 12-week schedule). For a user a few days in, the actual line
// is a tiny squiggle against the y-axis while a long dashed target
// stretches off to the right. We let the user pick a window —
// 'week' (today ±3 days), 'month' (28 days centered-ish on today), or
// 'full' — and pick a flattering default based on how long they've
// been tapering.

export type TaperWindowMode = 'week' | 'month' | 'full';

const VALID_MODES: ReadonlyArray<TaperWindowMode> = ['week', 'month', 'full'];

const STORAGE_KEY = 'qk.taper.window';
const STORAGE_OVERLAY_KEY = 'qk.taper.overlay';
const STORAGE_TIMING_KEY = 'qk.taper.timing-window';

export type DoseTimingWindow = '1d' | '3d' | '7d' | '14d';

const VALID_TIMING_WINDOWS: ReadonlyArray<DoseTimingWindow> = ['1d', '3d', '7d', '14d'];

export function timingWindowDays(mode: DoseTimingWindow): number {
  switch (mode) {
    case '1d': return 1;
    case '3d': return 3;
    case '7d': return 7;
    case '14d': return 14;
  }
}

export function readStoredTimingWindow(): DoseTimingWindow {
  if (typeof window === 'undefined') return '3d';
  try {
    const v = window.localStorage.getItem(STORAGE_TIMING_KEY);
    return VALID_TIMING_WINDOWS.includes(v as DoseTimingWindow)
      ? (v as DoseTimingWindow)
      : '3d';
  } catch {
    return '3d';
  }
}

export function writeStoredTimingWindow(mode: DoseTimingWindow): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_TIMING_KEY, mode);
  } catch {
    // Silent on storage failure — in-memory state is enough for the session.
  }
}

// Smart default. Only consulted when the user hasn't explicitly chosen
// a window yet. Picks the tightest range that still leaves the actual
// line dominant relative to the target line — so a new user lands on a
// chart that shows their day-to-day progress, not a 12-week marathon.
export function pickDefaultTaperWindow(todayIndex: number): TaperWindowMode {
  if (todayIndex < 7) return 'week';
  if (todayIndex < 28) return 'month';
  return 'full';
}

// Map a window mode + today's position + total plan length to the
// inclusive index range to show. Clamps to plan bounds; if today is
// near a boundary, the window shifts so today stays inside.
export function resolveTaperWindow(
  mode: TaperWindowMode,
  todayIndex: number,
  totalDays: number,
): { startIndex: number; endIndex: number } {
  const lastIndex = Math.max(0, totalDays - 1);
  if (mode === 'full' || totalDays <= 7) {
    return { startIndex: 0, endIndex: lastIndex };
  }
  const windowSize = mode === 'week' ? 7 : 28;
  // 'week': 3 before / 3 after.
  // 'month': 7 before / 20 after — biased forward so users see what's
  // coming, since the past is what the actual line covers.
  const before = mode === 'week' ? 3 : 7;
  let start = todayIndex - before;
  let end = start + windowSize - 1;
  if (end > lastIndex) {
    end = lastIndex;
    start = Math.max(0, end - windowSize + 1);
  }
  if (start < 0) {
    start = 0;
    end = Math.min(lastIndex, windowSize - 1);
  }
  return { startIndex: start, endIndex: end };
}

export function readStoredTaperWindow(): TaperWindowMode | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return VALID_MODES.includes(v as TaperWindowMode) ? (v as TaperWindowMode) : null;
  } catch {
    return null;
  }
}

export function writeStoredTaperWindow(mode: TaperWindowMode): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // Private-mode / quota — silent failure is fine; the segmented
    // control still reflects the in-memory state for this session.
  }
}

// "Compare to previous period" toggle persistence. Off by default —
// users opt in once they want week-over-week context.
export function readStoredTaperOverlay(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_OVERLAY_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeStoredTaperOverlay(on: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_OVERLAY_KEY, on ? '1' : '0');
  } catch {
    // Same rationale as writeStoredTaperWindow — silent on storage failure.
  }
}
